import type { OriginScope } from "@features/report/template/types/reportTemplate";
import type { KpiOperation, RollupMode } from "@features/admin/kpi/types/kpiEntryMode";

/** Keep in sync with PresentationLayoutConstants (ntcog-api). */
export const LAYOUT_DEFAULT = "default";

export const SECTION_TYPES = {
  FIELD_LIST: "FIELD_LIST",
  KPI_MATRIX: "KPI_MATRIX",
  DISTRIBUTION_TABLE: "DISTRIBUTION_TABLE",
  BINDING_TABLE: "BINDING_TABLE",
} as const;
export type SectionType = (typeof SECTION_TYPES)[keyof typeof SECTION_TYPES];

export const COLUMN_TYPES = {
  KPI_BINDING: "KPI_BINDING",
  SOURCE_VALUE: "SOURCE_VALUE",
  DEDUCTIBLE: "DEDUCTIBLE",
  CALCULATED: "CALCULATED",
  VALUE: "VALUE",
} as const;
export type ColumnType = (typeof COLUMN_TYPES)[keyof typeof COLUMN_TYPES];

export const TOTALS_MODES = {
  NONE: "NONE",
  COLUMN: "COLUMN",
  ROW: "ROW",
  BOTH: "BOTH",
} as const;
export type TotalsMode = (typeof TOTALS_MODES)[keyof typeof TOTALS_MODES];

export const DISTRIBUTION_ENGINES = {
  CATEGORY: "CATEGORY",
  ALLOCATION_RULE: "ALLOCATION_RULE",
} as const;
export type DistributionEngine = (typeof DISTRIBUTION_ENGINES)[keyof typeof DISTRIBUTION_ENGINES];

export const SECTION_TYPE_LABELS: Record<SectionType, string> = {
  FIELD_LIST: "Field list",
  KPI_MATRIX: "KPI matrix",
  DISTRIBUTION_TABLE: "Distribution table",
  BINDING_TABLE: "Binding table",
};

/** Short help copy for the layout editor Sections tab. Detailed tables live in the help footer. */
export const SECTION_TYPE_HELP: Record<SectionType, { summary: string; example: string }> = {
  FIELD_LIST: {
    summary: "A simple label-and-value list. Each row picks one template, activity, and KPI.",
    example: "Sunday Service · Attendance → 142",
  },
  KPI_MATRIX: {
    summary: "Same KPIs down each column. Each row is one activity. Optional row and column totals.",
    example: "Sunday Service / Bible Study × Attendance | Visitors",
  },
  DISTRIBUTION_TABLE: {
    summary:
      "For currency/quantity KPIs: source value, deductibles, and calculated columns (tithes use finance categories; other income uses allocation rules).",
    example: "Tithes · Gross | Island Office | District | Net",
  },
  BINDING_TABLE: {
    summary:
      "Column headers are labels only. Each cell picks its own template, activity, and KPI — or stays blank.",
    example: "General Information · $ | Total Attendance",
  },
};

export const COLUMN_TYPE_LABELS: Record<ColumnType, string> = {
  KPI_BINDING: "KPI",
  SOURCE_VALUE: "Source value",
  DEDUCTIBLE: "Deductible",
  CALCULATED: "Calculated",
  VALUE: "Value",
};

export const TOTALS_MODE_LABELS: Record<TotalsMode, string> = {
  NONE: "No totals",
  COLUMN: "Column totals",
  ROW: "Row totals",
  BOTH: "Row and column totals",
};

export const DISTRIBUTION_ENGINE_LABELS: Record<DistributionEngine, string> = {
  CATEGORY: "Finance categories",
  ALLOCATION_RULE: "Allocation rule",
};

export const ORIGIN_SCOPE_LABELS: Record<OriginScope, string> = {
  CHURCH: "Church",
  DISTRICT: "District",
  NATION: "Nation",
};

export const ORIGIN_SCOPES: OriginScope[] = ["CHURCH", "DISTRICT", "NATION"];

export type IncludeFlag = "include_church_templates" | "include_district_templates" | "include_nation_templates";

export const INCLUDE_FLAG_BY_ORIGIN: Record<OriginScope, IncludeFlag> = {
  CHURCH: "include_church_templates",
  DISTRICT: "include_district_templates",
  NATION: "include_nation_templates",
};

export type LayoutKpiMeta = {
  kpi_name: string;
  data_type: string | null;
  rollup_mode: RollupMode | null;
  decimal_places: number | null;
  unit_name: string | null;
  unit_abbreviation: string | null;
  unit_position: string | null;
};

export type PresentationLayoutIncludes = Record<IncludeFlag, boolean>;

export type PresentationLayoutListItem = PresentationLayoutIncludes & {
  presentation_layout_id: number;
  department_id: number;
  name: string;
  description: string | null;
  origin_scope: OriginScope;
  list_order: number;
  is_active: boolean;
  is_in_use: boolean;
  version: number;
  section_count: number;
  effective_from: string;
  created_at: string;
  created_by: number;
  updated_at: string | null;
  updated_by: number | null;
};

export type PresentationLayoutQueryResponse = {
  department_id: number;
  locked: boolean;
  locked_reason: string | null;
  data: PresentationLayoutListItem[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from?: number | null;
  to?: number | null;
};

export type PresentationLayoutQueryParams = {
  page?: number;
  perPage?: number;
  all?: boolean;
  freetext?: string;
  originScope?: OriginScope | string;
  isActive?: boolean;
  sortBy?: "list_order" | "name";
  sortDirection?: "asc" | "desc";
};

export type LayoutCellDetail = {
  cell_id?: number;
  is_empty: boolean;
  report_template_id: number | null;
  report_template_name?: string | null;
  template_origin_scope?: OriginScope | null;
  activity_id: number | null;
  activity_name?: string | null;
  kpi_id: number | null;
  kpi: LayoutKpiMeta | null;
};

export type LayoutRowDetail = {
  row_id: number;
  label: string;
  report_template_id: number | null;
  report_template_name: string | null;
  template_origin_scope: OriginScope | null;
  activity_id: number | null;
  activity_name: string | null;
  kpi_id: number | null;
  kpi: LayoutKpiMeta | null;
  distribution_engine?: DistributionEngine;
  /** Binding table: aligned 1:1 with columns; Calculated slots are null. */
  cells?: Array<LayoutCellDetail | null>;
};

export type LayoutColumnDetail = {
  column_id: number;
  label: string;
  column_type: ColumnType;
  kpi_id: number | null;
  kpi: LayoutKpiMeta | null;
  deductible_id: number | null;
  deductible_name: string | null;
  deductible_code: string | null;
  calc_operation: KpiOperation | null;
  /** 0-based indexes of earlier columns in the same section. */
  operands: number[];
};

export type LayoutSectionDetail = {
  section_id: number;
  title: string;
  section_type: SectionType;
  row_header_label: string | null;
  totals_mode: TotalsMode;
  totals_label: string | null;
  rows: LayoutRowDetail[];
  columns: LayoutColumnDetail[];
};

export type PresentationLayoutDetail = PresentationLayoutListItem & {
  locked: boolean;
  locked_reason: string | null;
  sections: LayoutSectionDetail[];
};

export type LayoutMetaKpi = LayoutKpiMeta & { kpi_id: number };

export type LayoutMetaTemplate = {
  report_template_id: number;
  report_template_name: string;
  origin_scope: OriginScope;
  activities: Array<{
    activity_id: number;
    activity_name: string;
    kpis: LayoutMetaKpi[];
  }>;
};

export type PresentationLayoutMeta = {
  section_types: SectionType[];
  column_types: ColumnType[];
  section_column_types: Record<SectionType, ColumnType[]>;
  totals_modes: TotalsMode[];
  section_totals_modes: Record<SectionType, TotalsMode[]>;
  calc_operations: Array<{ operation: KpiOperation; requires_exactly_two: boolean }>;
  origins: OriginScope[];
  allowed_includes: Record<OriginScope, OriginScope[]>;
  tithes_kpi_id: number;
  limits: { sections: number; rows_per_section: number; columns_per_section: number };
  templates: LayoutMetaTemplate[];
};

export type LayoutCellInput = {
  is_empty: boolean;
  report_template_id: number | null;
  activity_id: number | null;
  kpi_id: number | null;
};

export type LayoutRowInput = {
  label: string;
  report_template_id: number | null;
  activity_id: number | null;
  kpi_id: number | null;
  /** Binding table only: aligned 1:1 with columns; Calculated slots are null. */
  cells?: Array<LayoutCellInput | null>;
};

export type LayoutColumnInput = {
  label: string;
  column_type: ColumnType;
  kpi_id: number | null;
  deductible_id: number | null;
  calc_operation: KpiOperation | null;
  operands: number[];
};

export type LayoutSectionInput = {
  title: string;
  section_type: SectionType;
  row_header_label: string | null;
  totals_mode: TotalsMode;
  totals_label: string | null;
  rows: LayoutRowInput[];
  columns: LayoutColumnInput[];
};

export type UpsertPresentationLayoutPayload = PresentationLayoutIncludes & {
  name: string;
  description: string | null;
  origin_scope?: OriginScope;
  sections: LayoutSectionInput[];
};

/** Rendered layout on the department report GET (`data.layout`). Values are null in the admin preview. */
export type RenderedLayoutCellValue = number | string | null;

export type RenderedLayoutColumn = {
  column_id: number;
  label: string;
  column_type: ColumnType;
  kpi: LayoutKpiMeta | null;
  deductible_id: number | null;
  deductible_name: string | null;
  calc_operation: KpiOperation | null;
  operands: number[];
};

type RenderedSectionHeader = {
  section_id: number;
  title: string;
  row_header_label: string | null;
  totals_mode: TotalsMode;
  totals_label: string | null;
};

export type RenderedFieldListSection = RenderedSectionHeader & {
  section_type: typeof SECTION_TYPES.FIELD_LIST;
  rows: Array<{ row_id: number; label: string; kpi: LayoutKpiMeta | null; value: RenderedLayoutCellValue }>;
};

export type RenderedTableSection = RenderedSectionHeader & {
  section_type:
    | typeof SECTION_TYPES.KPI_MATRIX
    | typeof SECTION_TYPES.DISTRIBUTION_TABLE
    | typeof SECTION_TYPES.BINDING_TABLE;
  columns: RenderedLayoutColumn[];
  rows: Array<{
    row_id: number;
    label: string;
    cells: RenderedLayoutCellValue[];
    /** Binding table: KPI meta per Value cell for formatting. */
    cell_kpis?: Array<LayoutKpiMeta | null>;
    row_total?: number | null;
    kpi?: LayoutKpiMeta | null;
    distribution_engine?: DistributionEngine;
  }>;
  column_totals: Array<number | null> | null;
  grand_total: number | null;
};

export type RenderedLayoutSection = RenderedFieldListSection | RenderedTableSection;

export type RenderedLayout = {
  presentation_layout_id: number;
  layout_version_id: number;
  version: number;
  name: string;
  reference_moment: string;
  bindings_incomplete?: boolean;
  sections: RenderedLayoutSection[];
};

export type AvailableLayoutOption = {
  layout: string;
  presentation_layout_id: number | null;
  name: string;
  /** Null on the shared Default entry. Named layouts carry the tab they belong on. */
  origin_scope?: "CHURCH" | "DISTRICT" | "NATION" | null;
  bindings_incomplete?: boolean;
};
